import { useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const useResourceVoucher = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const getAllResourceVouchers = async (
    searchQuery = '',
    page = 1,
    limit = 20,
    selectKeys = [],
    onlyMine = false
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const params = {};
        if (searchQuery) params.searchQuery = searchQuery;
        if (page) params.page = page;
        if (limit) params.limit = limit;
        if (selectKeys.length > 0) params.selectKeys = selectKeys.join(',');
        if (onlyMine) params.onlyMine = 'true';
        const responseData = await sendRequest({
          url: '/api/v1/resourceVoucher',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params,
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getResourceVoucherById = async voucherId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/resourceVoucher/${voucherId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const createResourceVoucher = async (
    voucherData,
    toastMessage = null,
    onStored = null,
    displayInfo = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest(
          {
            url: '/api/v1/resourceVoucher',
            method: 'POST',
            headers: authCtx.getAuthHeaders(),
            body: voucherData,
          },
          toastMessage,
          onStored,
          displayInfo
        );
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getResourceVoucherDownloadURL = async (
    voucherId,
    toastMessage = null,
    onStored = null,
    displayInfo = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest(
          {
            url: `/api/v1/resourceVoucher/${voucherId}/download`,
            method: 'GET',
            headers: authCtx.getAuthHeaders(),
          },
          toastMessage,
          onStored,
          displayInfo
        );
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getResourceVouchersZipURL = async (
    voucherIds,
    toastMessage = null,
    onStored = null,
    displayInfo = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest(
          {
            url: '/api/v1/resourceVoucher/batch/download',
            method: 'POST',
            headers: authCtx.getAuthHeaders(),
            body: { voucherIds },
          },
          toastMessage,
          onStored,
          displayInfo
        );
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const cancelResourceVoucherById = async (resourceVoucherId, reason = null) => {
    return sendRequest({
      url: `/api/v1/resourceVoucher/${resourceVoucherId}/cancel`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body: reason ? { reason } : {},
    });
  };

  // Resend the resource voucher PDF to the email address already stored on the voucher
  const resendResourceVoucherEmail = async resourceVoucherId => {
    return sendRequest({
      url: `/api/v1/resourceVoucher/${resourceVoucherId}/resend-email`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body: {},
    });
  };

  // Update the buyer email address stored on the resource voucher
  const updateResourceVoucherBuyerEmail = async (resourceVoucherId, email) => {
    return sendRequest({
      url: `/api/v1/resourceVoucher/${resourceVoucherId}/buyer-email`,
      method: 'PATCH',
      headers: authCtx.getAuthHeaders(),
      body: { email },
    });
  };

  const sellOfflineResourceVoucher = async voucherData => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/resourceVoucher/sell-offline',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: voucherData,
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getResourceVoucherMonthlyRedemptionReport = async (year, month) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/resourceVoucher/report/monthly-redemptions',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { year, month },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getResourceVoucherStatistics = async (
    startDate,
    endDate,
    resourceVoucherProductId = null
  ) => {
    const params = { startDate, endDate };
    if (resourceVoucherProductId) {
      params.resourceVoucherProductId = resourceVoucherProductId;
    }
    return sendRequest({
      url: '/api/v1/resourceVoucher/statistics',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
      params,
    });
  };

  return {
    isLoading,
    error,
    getAllResourceVouchers,
    getResourceVoucherById,
    createResourceVoucher,
    getResourceVoucherDownloadURL,
    getResourceVouchersZipURL,
    cancelResourceVoucherById,
    resendResourceVoucherEmail,
    updateResourceVoucherBuyerEmail,
    sellOfflineResourceVoucher,
    getResourceVoucherMonthlyRedemptionReport,
    getResourceVoucherStatistics,
  };
};

export default useResourceVoucher;
